import {
	Component,
	ElementRef,
	OnDestroy,
	OnInit,
	ViewChild
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
// Store
import { Store} from '@ngxs/store';
import { ClearChatSession, SetChatSession } from '@cv-invision/cv.invision-views.ux';
// Other
import { BroadcastEventModel, BroadcastService } from '@cv-invision/core';
// Service
import { ChatBotService } from './service/chat-bot.service';
// Model
import {
	ClassAssistantMessage,
	DataSource,
	Message
} from './model/message.model';

@Component({
	selector: 'cv-invision-ux-chatbot',
	templateUrl: './chatbot.component.html',
	styleUrls: ['./chatbot.component.scss'],
})
export class ChatBotComponent implements OnInit, OnDestroy {
	@ViewChild('messagesContainer') private messagesContainer: ElementRef;
	chatForm: FormGroup;
	dataSources: DataSource[] = [];
	messages: Message[] = [];

	private orderId: number = null;
	private atComponentDestroy = new Subject();

	constructor(
		private formBuilder: FormBuilder,
		private broadcastService: BroadcastService,
		private chatBotService: ChatBotService,
		private store: Store,
	) {
		this.broadcastService
			.onEvent('CHATBOT')
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((e: BroadcastEventModel) => {
				let useExistentDataSources;

				if (e.event.orderId) {
					this.orderId = e.event.orderId;
					const chatSession = this.getChatSession();
					if (chatSession && chatSession.orderId !== this.orderId) {
						this.messages = [];
						this.store.dispatch(new ClearChatSession());
					}
				} else {
					this.orderId = null;
					this.messages = [];
					this.store.dispatch(new ClearChatSession());
				}

				if (e.event.useExistentDataSources) {
					useExistentDataSources = e.event.useExistentDataSources;
				}

				this.getDataSources(this.orderId, useExistentDataSources);
			});
	}

	/**
	 * OnInit event
	 */
	ngOnInit(): void {
		this.chatForm = this.formBuilder.group({
			'content': ['', [Validators.required]],
			'datasource': ['', [Validators.required]]
		});
	}

	/**
	 * event to get datasources to display in the dropdown
	 * @param orderId
	 * @param useExistentDataSources
	 * @private
	 */
	private getDataSources(orderId?: number, useExistentDataSources = false): void {
		if (useExistentDataSources && this.dataSources.length) {
			// do nothing, for now
		} else {
			this.chatBotService.getDataSources(orderId)
				.pipe(takeUntil(this.atComponentDestroy))
				.subscribe((response: any) => {
					if (response.dataSources) {
						this.dataSources = response.dataSources;
					}
				});
		}
	}

	/**
	 * Sends a message through the chat form if the form is valid. It handles form validation, appends the user's input
	 * and bot's response to the message list, and maintains the session with the backend chat service. The method
	 * also scrolls to the bottom of the chat view after sending or receiving a message.
	 *
	 * @return {void} Does not return a value. The operation modifies the message list and updates the chat interface directly.
	 */
	sendMessage(): void {

		if (this.chatForm.valid) {
			const body = this.chatForm.value;
			this.messages.push(body);
			const message = new Message();
			message.isLoading = true;
			message.isBot = true;
			message.datasource = body.datasource;
			this.clearForm();
			this.messages.push(message);
			this.scrollToBottom();

			const classAssistantMessage: ClassAssistantMessage = {
				isAssistant: false,
				text: body.content
			}
			// Prepare Post-Data
			const postData: { sessionId?: string | number, data: ClassAssistantMessage[] } = {
				data: [classAssistantMessage]
			};
			// If session exists keep the session
			const chatSession = this.getChatSession();
			if (chatSession && chatSession.sessionId) {
				postData.sessionId = chatSession.sessionId ;
			}

			this.chatBotService.sendMessage(body.datasource?.vectorDocumentId, postData)
				.pipe(takeUntil(this.atComponentDestroy))
				.subscribe((result) => {
					if (result && result.success) {
						message.content = result.response;
						message.isLoading = false;

						if (result.refs) {
							message.refs = result.refs;
						}

						// Preserve Chat Session
						this.keepChatSession(result.sessionId);
					} else {
						message.content = result.error;
						message.isLoading = false;
					}
					this.scrollToBottom();
				})
		}
	}

	/**
	 * Maintains or updates the current chat session for the chatbot by comparing the existing session ID
	 * and order ID with the provided session ID. If they differ, it updates the session with the new
	 * session ID and order ID.
	 *
	 * @param {string} sessionId - The unique identifier of the chat session to be maintained or updated.
	 * @return {void} No return value.
	 */
	private keepChatSession(sessionId: string): void {
		const chatSession = this.store.selectSnapshot(state => state.chatbot);
		if (chatSession && this.orderId !== null &&
			(
				chatSession.sessionId !== sessionId ||
				chatSession.orderId !== this.orderId
			)
		) {
			this.store.dispatch(new SetChatSession(sessionId, this.orderId));
		}
	}

	/**
	 * Retrieves the current chat session from the state store.
	 *
	 * @return {any} The chat session object if available; otherwise, returns null.
	 */
	private getChatSession(): any {
		const chatSession = this.store.selectSnapshot(state => state.chatbot);
		return chatSession || null;
	}

	/**
	 * event to clearForm to default state
	 * @private
	 */
	private clearForm(): void {
		this.chatForm.get('content').reset('');
		this.chatForm.markAsPristine();
		this.chatForm.markAsUntouched();
	}

	/**
	 * Scrolls the messages container element to its bottom-most position.
	 * Useful for ensuring that the latest content within the container is visible to the user.
	 *
	 * @return {void} Does not return any value.
	 */
	scrollToBottom(): void {
		setTimeout(() => {
			this.messagesContainer.nativeElement.scrollTop = this.messagesContainer.nativeElement.scrollHeight;
		})
	}

	/**
	 * Method invoked automatically during the Angular component lifecycle when the component is about to be destroyed.
	 * It performs cleanup activities such as emitting a completion signal and unsubscribing from observables to release resources and prevent memory leaks.
	 *
	 * @return {void} This method does not return any value.
	 */
	ngOnDestroy(): void {
		try {
			this.atComponentDestroy.next({});
			this.atComponentDestroy.unsubscribe();
		} catch (error) {
			console.error(error);
		}

	}
}
